import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle } from "lucide-react";
import type { CaseStudy } from "@/components/CaseStudyStrip";

interface CaseStudyModalProps {
  caseStudy: CaseStudy | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CaseStudyModal = ({ caseStudy, open, onOpenChange }: CaseStudyModalProps) => {
  if (!caseStudy) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4 mb-4">
            <img
              src={caseStudy.image}
              alt={caseStudy.businessName}
              className="w-20 h-20 rounded-full object-cover"
            />
            <div className="flex-1">
              <DialogTitle className="text-2xl mb-2">
                {caseStudy.businessName}
              </DialogTitle>
              <Badge variant="outline">{caseStudy.industry}</Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* ROI Section */}
          <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
            <h3 className="font-semibold text-lg text-primary mb-2">
              💰 Retorno de Inversión
            </h3>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-primary">
                {caseStudy.roi.value}
              </p>
              <p className="text-sm text-muted-foreground">
                {caseStudy.roi.metric} · {caseStudy.roi.timeframe}
              </p>
            </div>
          </div>

          {/* Before Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <XCircle className="w-5 h-5 text-destructive" />
              <h3 className="font-semibold text-lg">{caseStudy.before.title}</h3>
            </div>
            <ul className="space-y-2">
              {caseStudy.before.problems.map((problem, index) => (
                <li
                  key={index}
                  className="flex gap-2 text-sm text-muted-foreground pl-2 border-l-2 border-destructive/30"
                >
                  <span>•</span>
                  <span>{problem}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* After Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-lg">{caseStudy.after.title}</h3>
            </div>
            <ul className="space-y-2">
              {caseStudy.after.solutions.map((solution, index) => (
                <li
                  key={index}
                  className="flex gap-2 text-sm text-muted-foreground pl-2 border-l-2 border-primary/30"
                >
                  <span>•</span>
                  <span>{solution}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Testimonial Section */}
          <div className="bg-muted/50 rounded-lg p-4 border">
            <div className="flex items-start gap-2 mb-3">
              <span className="text-2xl">💬</span>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">Testimonio</h3>
                <blockquote className="text-muted-foreground italic mb-3">
                  "{caseStudy.testimonial.quote}"
                </blockquote>
                <div className="text-sm">
                  <p className="font-medium text-foreground">
                    {caseStudy.testimonial.author}
                  </p>
                  <p className="text-muted-foreground">
                    {caseStudy.testimonial.role}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CaseStudyModal;
